import { extendTheme, type Theme } from '@mui/material/styles';

declare module '@mui/material/styles' {
  interface Palette {
    surface: {
      grid: string;
      panelBorder: string;
      soft: string;
      softStrong: string;
      accentGlow: string;
      muted: string;
      hero: string;
      heroText: string;
      heroMuted: string;
      primarySoft: string;
      successSoft: string;
      blueSoft: string;
      shadow: string;
    };
  }

  interface PaletteOptions {
    surface?: {
      grid?: string;
      panelBorder?: string;
      soft?: string;
      softStrong?: string;
      accentGlow?: string;
      muted?: string;
      hero?: string;
      heroText?: string;
      heroMuted?: string;
      primarySoft?: string;
      successSoft?: string;
      blueSoft?: string;
      shadow?: string;
    };
  }

  interface Shape {
    card: number;
    surface: number;
  }

  interface ShapeOptions {
    card?: number;
    surface?: number;
  }
}

const dashboardLightPalette = {
  primary: {
    main: '#674bdb',
    light: '#8974e0',
    dark: '#4f3da7',
    contrastText: '#fefdfe',
  },
  secondary: {
    main: '#84ccbe',
    light: '#a7ded6',
    dark: '#4ba99a',
    contrastText: '#142a28',
  },
  background: {
    default: '#f6f7fb',
    paper: '#ffffff',
  },
  text: {
    primary: '#535667',
    secondary: '#7a7e8b',
  },
  error: {
    main: '#d6606f',
  },
  success: {
    main: '#84ccbe',
  },
  divider: '#e1e5ee',
  surface: {
    grid: 'rgba(103, 75, 219, 0.05)',
    panelBorder: '#dfe4ee',
    soft: '#f1f3f8',
    softStrong: '#e9edf5',
    accentGlow: 'rgba(103, 75, 219, 0.28)',
    muted: '#afb3be',
    hero: '#120c3b',
    heroText: '#fefdfe',
    heroMuted: 'rgba(255, 255, 255, 0.62)',
    primarySoft: '#f2efff',
    successSoft: '#eaf8f5',
    blueSoft: '#ecf4fd',
    shadow: '0 18px 45px rgba(47, 42, 82, 0.1)',
  },
};

export const appTheme = extendTheme({
  cssVarPrefix: 'decentro',
  colorSchemeSelector: 'class',
  defaultColorScheme: 'light',
  colorSchemes: {
    light: {
      palette: dashboardLightPalette,
    },
    dark: {
      palette: {
        primary: {
          main: '#8974e0',
          light: '#aa9af0',
          dark: '#674bdb',
          contrastText: '#fefdfe',
        },
        secondary: {
          main: '#84ccbe',
          light: '#a7ded6',
          dark: '#4ba99a',
          contrastText: '#0e1f1d',
        },
        background: {
          default: '#0e0b1d',
          paper: '#1b1730',
        },
        text: {
          primary: '#f7f6fb',
          secondary: '#b7b4c7',
        },
        error: {
          main: '#ef8190',
        },
        success: {
          main: '#84ccbe',
        },
        divider: 'rgba(255, 255, 255, 0.14)',
        surface: {
          grid: 'rgba(137, 116, 224, 0.08)',
          panelBorder: 'rgba(255, 255, 255, 0.16)',
          soft: 'rgba(255, 255, 255, 0.07)',
          softStrong: 'rgba(255, 255, 255, 0.12)',
          accentGlow: 'rgba(137, 116, 224, 0.32)',
          muted: '#8c879f',
          hero: '#120c3b',
          heroText: '#fefdfe',
          heroMuted: 'rgba(255, 255, 255, 0.62)',
          primarySoft: 'rgba(137, 116, 224, 0.16)',
          successSoft: 'rgba(132, 204, 190, 0.12)',
          blueSoft: 'rgba(87, 138, 231, 0.12)',
          shadow: '0 20px 48px rgba(0, 0, 0, 0.38)',
        },
      },
    },
  },
  shape: {
    borderRadius: 8,
    card: 16,
    surface: 8,
  },
  typography: {
    fontFamily: ['Inter', 'Segoe UI Variable', 'Segoe UI', 'sans-serif'].join(
      ','
    ),
    h1: {
      fontWeight: 500,
      letterSpacing: '-0.03em',
      lineHeight: 1.16,
    },
    h2: {
      fontWeight: 600,
      letterSpacing: '-0.025em',
      lineHeight: 1.18,
    },
    h3: {
      fontWeight: 600,
      letterSpacing: '-0.018em',
    },
    h4: {
      fontWeight: 700,
      letterSpacing: '-0.015em',
    },
    body1: {
      lineHeight: 1.55,
    },
    body2: {
      lineHeight: 1.5,
    },
    button: {
      fontWeight: 500,
      textTransform: 'none',
    },
    caption: {
      lineHeight: 1.35,
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: ({ theme }: { theme: Theme }) => ({
          backgroundColor: theme.vars!.palette.background.default,
          backgroundImage: 'none',
          color: theme.vars!.palette.text.primary,
        }),
      },
    },
    MuiPaper: {
      defaultProps: {
        elevation: 0,
        variant: 'outlined',
      },
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
        outlined: ({ theme }: { theme: Theme }) => ({
          borderColor: theme.vars!.palette.surface.panelBorder,
        }),
      },
    },
    MuiCard: {
      styleOverrides: {
        root: ({ theme }: { theme: Theme }) => ({
          borderRadius: theme.shape.card,
          borderColor: theme.vars!.palette.surface.panelBorder,
          backgroundColor: theme.vars!.palette.background.paper,
          boxShadow: theme.vars!.palette.surface.shadow,
          backdropFilter: 'none',
        }),
      },
    },
    MuiAppBar: {
      defaultProps: {
        color: 'default',
        elevation: 0,
      },
      styleOverrides: {
        root: ({ theme }: { theme: Theme }) => ({
          backgroundColor: theme.vars!.palette.background.paper,
          backgroundImage: 'none',
          color: theme.vars!.palette.text.primary,
          borderBottom: `1px solid ${theme.vars!.palette.surface.panelBorder}`,
          boxShadow: 'none',
        }),
      },
    },
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: ({ theme }: { theme: Theme }) => ({
          borderRadius: theme.shape.surface,
          minHeight: 36,
          paddingInline: 18,
          fontSize: '0.8125rem',
        }),
        outlined: ({ theme }: { theme: Theme }) => ({
          borderColor: theme.vars!.palette.surface.panelBorder,
        }),
      },
    },
    MuiTextField: {
      defaultProps: {
        variant: 'outlined',
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: ({ theme }: { theme: Theme }) => ({
          borderRadius: theme.shape.surface,
          backgroundColor: theme.vars!.palette.background.paper,
        }),
        notchedOutline: ({ theme }: { theme: Theme }) => ({
          borderColor: theme.vars!.palette.surface.panelBorder,
        }),
      },
    },
    MuiChip: {
      styleOverrides: {
        root: ({ theme }: { theme: Theme }) => ({
          borderRadius: theme.shape.surface,
          minHeight: 24,
          color: theme.vars!.palette.text.secondary,
          backgroundColor: theme.vars!.palette.surface.primarySoft,
        }),
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: ({ theme }: { theme: Theme }) => ({
          backgroundColor: theme.vars!.palette.primary.main,
        }),
      },
    },
    MuiTab: {
      styleOverrides: {
        root: ({ theme }: { theme: Theme }) => ({
          minHeight: 40,
          color: theme.vars!.palette.text.secondary,
          fontSize: '0.8125rem',
          '&.Mui-selected': {
            color: theme.vars!.palette.primary.main,
          },
        }),
      },
    },
  },
});
